import React, { useState, useEffect, useContext } from 'react';
import { Col, Row } from '@flightstats/airframe-react';

import {
  Tabset,
  TabList,
  NavTab,
  TabContent,
  TabPanel,
} from '../../components/tabs/Tabs';
import Input, {
  FormLabeledInputRowWrapper,
} from '../../components/forms/Input';
import { BodyCard } from '../components/styledComponent';
import type {
  MessageConfigurationProps,
  PatchMessageConfigurationValuesRequest,
} from '../../types/messageConfiguration';
import EmailOptinForm from './EmailOptinForm';
import EmailReminderForm from './EmailReminderForm';
import EmailItineraryForm from './EmailItineraryForm';
import SearchBody from './SearchBody';
import { PageContext } from '../components/page';
import { MessageConfigurationWithUrlPath } from '../../appRoutes';
import ActionRow from './ActionRow';
import { ButtonPrimary } from '../../components/forms/Buttons';
import { createMessageConfig } from '../../service/messageConfigurationService';

const MessageConfigurationForm = ({
  messageConfigs = [],
  domainList,
  emailList,
  onSave,
}: MessageConfigurationProps) => {
  const { navigate, pushAlert } = useContext(PageContext);

  const [emailItineraryTabSelected, setEmailItineraryTabSelected] = useState(
    true,
  );
  const [emailOptinTabSelected, setEmailOptinTabSelected] = useState(false);
  const [emailReminderTabSelected, setEmailReminderTabSelected] = useState(
    false,
  );
  const [messageConfigId, setMessageConfigId] = useState<string>('');
  const [selectedMessageConfig, setSelectedMessageConfig] = useState<any>();
  const [name, setName] = useState('');

  useEffect(() => {
    if (messageConfigId) {
      const selectedConfig = messageConfigs.find(config => messageConfigId === config.id);
      setName(selectedConfig?.name ?? '');
      setSelectedMessageConfig(selectedConfig);
      navigate(
        MessageConfigurationWithUrlPath.replace(
          ':messageConfigurationId',
          messageConfigId,
        ),
      );
    }
  }, [messageConfigId, messageConfigs]);

  const updateMessageConfig = (
    patchRequest: PatchMessageConfigurationValuesRequest,
  ): void => {
    if (selectedMessageConfig?.id) {
      onSave(selectedMessageConfig.id, patchRequest);
    }
  };

  const saveName = (): void => {
    if (selectedMessageConfig?.id) {
      const patchRequest: PatchMessageConfigurationValuesRequest = {
        name: name.trim(),
      };
      onSave(selectedMessageConfig.id, patchRequest);
    }
  };

  const createConfig = async (
    messageConfigurationId: string,
    name: string,
    isClone: boolean = true,
  ): Promise<void> => {
    try {
      const response = await createMessageConfig(messageConfigurationId, name, isClone);
      navigate(
        MessageConfigurationWithUrlPath.replace(':messageConfigurationId', response),
      );
      location.reload();
    } catch (err: any) {
      const message = err.message.includes('already exists')
        ? `Unable to create configuration with name "${name}" as it already exists. Please update that configuration name first.`
        : err.message;
      pushAlert('danger', message);
    }
  };

  return (
    <>
      {(messageConfigs.length && (
        <>
          <SearchBody
            messageConfigs={messageConfigs}
            onSelect={(id: string) => setMessageConfigId(id)}
            cloneConfig={createConfig}
          />
          <BodyCard>
            <Row>
              <Col>
                <FormLabeledInputRowWrapper
                  elementId="nameId"
                  label="Configuration Name:"
                >
                  <Input
                    id="nameId"
                    name="nameId"
                    value={name}
                    onChange={e => setName(e?.currentTarget?.value)}
                  />
                </FormLabeledInputRowWrapper>
              </Col>
              <Col align="flex-end">
                <ActionRow
                  id="nameButtons"
                  onCancel={() => setName('')}
                  onSave={saveName}
                />
              </Col>
            </Row>
          </BodyCard>
          <BodyCard>
            <Tabset>
              <TabList>
                <NavTab
                  name="Trip Alert Email"
                  active={emailItineraryTabSelected}
                  onSelect={() => {
                    setEmailReminderTabSelected(false);
                    setEmailOptinTabSelected(false);
                    setEmailItineraryTabSelected(true);
                  }}
                />
                <NavTab
                  name="Opt-in Email"
                  active={emailOptinTabSelected}
                  onSelect={() => {
                    setEmailReminderTabSelected(false);
                    setEmailOptinTabSelected(true);
                    setEmailItineraryTabSelected(false);
                  }}
                />

                <NavTab
                  name="Trip Reminder Email"
                  active={emailReminderTabSelected}
                  onSelect={() => {
                    setEmailReminderTabSelected(true);
                    setEmailOptinTabSelected(false);
                    setEmailItineraryTabSelected(false);
                  }}
                />
              </TabList>
              <TabContent>
                <TabPanel active={emailItineraryTabSelected}>
                  <EmailItineraryForm
                    emailItinerary={
                      selectedMessageConfig?.messageConfigurationValues
                        ?.emailItinerary
                    }
                    domainList={domainList}
                    emailList={emailList}
                    onSave={updateMessageConfig}
                  />
                </TabPanel>
                <TabPanel active={emailOptinTabSelected}>
                  <EmailOptinForm
                    emailOptin={
                      selectedMessageConfig?.messageConfigurationValues
                        ?.emailOptin
                    }
                    domainList={domainList}
                    emailList={emailList}
                    onSave={updateMessageConfig}
                  />
                </TabPanel>
                <TabPanel active={emailReminderTabSelected}>
                  <EmailReminderForm
                    emailReminder={
                      selectedMessageConfig?.messageConfigurationValues
                        ?.emailReminder
                    }
                    domainList={domainList}
                    emailList={emailList}
                    onSave={updateMessageConfig}
                  />
                </TabPanel>
              </TabContent>
            </Tabset>
          </BodyCard>
        </>
      )) || (
        <BodyCard>
          <ButtonPrimary
            id="createDefaultButton"
            onClick={() => createConfig('', 'Default', false)}
            type="button"
          >
            Create Default Message Configuration
          </ButtonPrimary>
        </BodyCard>
      )}
    </>
  );
};

export default MessageConfigurationForm;